import React, { useState, useEffect } from "react";
import styled from "styled-components";

import Tooltip from "./Tooltip";

import microApi from "../helpers/microApi";
import { percentChange, prettyPercent, getWhens, capitalize } from "../helpers/utils";

const StyledTable = styled.table`
  border-collapse: collapse;
  font-size: 0.9em;
  text-align: left;
  width: 100%;

  td, th {
    border-bottom: solid #d8d8d8 1px;
    padding: 1em 0.5em;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  td:first-child, th:first-child {
    padding-left: 0;
    text-align: left;
  }
  td:last-child, th:last-child {
    padding-right: 0;
  }
`;

const Header = styled.h3`
  margin: 3rem 0 1rem 0;
  font-size: 1.5rem;
`;

const Table = ({ rows, timeframe }) => (
  <StyledTable>
    <thead>
      <tr>
        <th>Name</th>
        <th>Postings</th>
        <th>
          {capitalize(timeframe)} over {timeframe} change
        </th>
        <th>Change from last year</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(({ name, unique_postings, change, yearChange }, i) => (
        <tr key={name}>
          <td>{name}</td>
          <td>{unique_postings.toLocaleString()}</td>
          <td>{prettyPercent(change)}</td>
          <td>{prettyPercent(yearChange)}</td>
        </tr>
      ))}
    </tbody>
  </StyledTable>
);

const RankingTable = ({ title, facet, facetLabel, exclude, filters, timeframe, latestDay, nation }) => {
  const [data, setData] = useState();

  useEffect(() => {
    let cancel = false;
    const getData = async () => {
      const { currWhen, prevWhen, yearAgoWhen } = getWhens(timeframe, latestDay);

      const rank = { limit: 0 };
      if (exclude) {
        rank.exclude = [exclude];
      }

      const res = await Promise.all([currWhen, prevWhen, yearAgoWhen].map(when => microApi.post(`${nation}/rankings/${facet}${nation === "us" ? "?soc_version=soc_emsi_2019" : ""}`, {
        filter: { when, ...filters },
        rank
      })));
      const rankings = res.map(r => r.data.data.ranking.buckets);
      
      if (cancel) return;
      setData({
        rows: rankings[0].map(({ name, unique_postings }) => {
          const prevBucket = rankings[1].find(b => b.name === name);
          const yearAgoBucket = rankings[2].find(b => b.name === name);
          return {
            name,
            unique_postings,
            change: prevBucket ? percentChange(prevBucket.unique_postings, unique_postings) : undefined,
            yearChange: yearAgoBucket ? percentChange(yearAgoBucket.unique_postings, unique_postings) : undefined
          };
        })
          .sort((a, b) => b.unique_postings - a.unique_postings),
        timeframe
      });
    };
    getData();
    return () => cancel = true;
  }, [facet, filters, timeframe, latestDay, exclude, nation]);

  return data ? (
    <div>
      <Header>
        Top {title}
        <Tooltip id={title+"-table-tooltip"}>
          {facetLabel} {title.toLowerCase()} sorted
          <br />
          by posting count (descending).
        </Tooltip>
      </Header>
      {data.rows.length ? (
        <Table rows={data.rows.slice(0, 10)} timeframe={data.timeframe} />
      ) : (
        <em>Insufficient data</em>
      )}
    </div>
  ) : null;
};

export default RankingTable;
